import { safeLocalStorage } from '@/lib/storage/safe-storage';
import { safeRandomUUID } from '@/lib/utils/safe-uuid';

const ANON_RESOURCE_KEY = 'bazak-anon-resource-id';

/**
 * The durable owner id for chat memory / thread history.
 *
 * PostHog's distinct id when analytics is active (unchanged for every tenant). For an
 * analytics-less browser the fallback depends on the tenant's persistence mode:
 *
 * - `server` (the opt-in server-persistence bundle, e.g. t19): a persistent per-browser
 *   random id, so anonymous users never share a server-side resource bucket (the shared
 *   `anonymous-<env>` constant was a cross-user history leak).
 * - `local`/default (all production tenants today): the historical `anonymous-<env>` constant,
 *   kept byte-identical — these tenants never read server thread history, so the per-browser id
 *   would be a behavior change for no benefit.
 */
export function resolveResourceId(
  distinctId: string | null | undefined,
  options?: { serverPersistence?: boolean },
): string {
  if (distinctId) return distinctId;
  if (!options?.serverPersistence) return `anonymous-${process.env.NODE_ENV}`;
  const existing = safeLocalStorage.getItem(ANON_RESOURCE_KEY);
  if (existing) return existing;
  const minted = `anon-${safeRandomUUID()}`;
  safeLocalStorage.setItem(ANON_RESOURCE_KEY, minted);
  return minted;
}

/** `resolveResourceId` for the server-persistence path — keeps the `serverPersistence` flag from
 *  drifting across the several resumable/server callers that all need it. */
export function resolveServerResourceId(distinctId: string | null | undefined): string {
  return resolveResourceId(distinctId, { serverPersistence: true });
}
